import { placeAttendanceUnderCalendar } from "@/lib/attendance-placement";
import { placeCareUnderPeople } from "@/lib/care-placement";
import { placeGroupManagementUnderPeople } from "@/lib/groups-placement";
import { placeRoomBookingsUnderCalendar } from "@/lib/room-bookings-placement";

/**
 * Which routes each module owns in the navigation. The sidebar (`navigation` and the Settings
 * entry in AppSidebar.tsx) takes its `href` and `matchPrefixes` from here, and so does the module
 * accent of a page under the next chassis (`getModuleForRoute`, src/components/layout/moduleForRoute.ts):
 * the sidebar highlight and the page's accent come from one table and cannot disagree (#7912).
 *
 * Keys are the module ids of `moduleAccentMap` (src/lib/module-accents.ts). Operations is
 * "projects" there, and Settings is the bottom-pinned entry, not one of `navigation`.
 */
export type NavModuleId =
  | "home"
  | "calendar"
  | "people"
  | "hr"
  | "finance"
  | "schools"
  | "ministry"
  | "projects"
  | "messaging"
  | "reports"
  | "settings";

interface NavModuleRoute {
  href: string;
  matchPrefixes?: string[];
}

const NAV_MODULE_ROUTES: Record<NavModuleId, NavModuleRoute> = {
  home: { href: "/today" },
  calendar: { href: "/events", matchPrefixes: ["/events", "/calendar"] },
  people: { href: "/people", matchPrefixes: ["/community", "/people", "/engagement"] },
  hr: { href: "/hr", matchPrefixes: ["/hr"] },
  finance: { href: "/finance/dashboard", matchPrefixes: ["/finance", "/contributions", "/pledges", "/funds"] },
  schools: { href: "/schools", matchPrefixes: ["/schools"] },
  ministry: {
    href: "/ministry",
    matchPrefixes: ["/ministry", "/services", "/volunteers", "/serving-teams", "/groups", "/attendance", "/kids-check-in", "/sermons"],
  },
  projects: {
    href: "/dashboard",
    matchPrefixes: ["/dashboard", "/forms", "/documents", "/requests", "/operations", "/tasks", "/projects", "/automations", "/room-bookings", "/assets"],
  },
  messaging: { href: "/messaging", matchPrefixes: ["/messaging", "/direct-messages", "/announcements", "/bulletins", "/communications"] },
  reports: { href: "/reports", matchPrefixes: ["/reports"] },
  settings: { href: "/settings", matchPrefixes: ["/settings"] },
};

/**
 * The Member Portal entry has no route of its own: its children are the pages. They are `/member/…`,
 * plus Give Now and Sermons under `/my`.
 */
const MEMBER_PORTAL_PREFIXES = ["/member", "/my/giving", "/my/sermons"];

/** A sidebar entry's `href` and `matchPrefixes` (fresh arrays: the placement helpers copy, but an entry may be edited). */
export function navRoute(module: NavModuleId): { href: string; matchPrefixes?: string[] } {
  const { href, matchPrefixes } = NAV_MODULE_ROUTES[module];
  return matchPrefixes ? { href, matchPrefixes: [...matchPrefixes] } : { href };
}

/** The sidebar's routes as they are placed under ui_uplift = next (docs/design/MODULE_MAPS.md). */
export function placeForNextChassis<T extends { href?: string; matchPrefixes?: string[] }>(items: T[]): T[] {
  return placeCareUnderPeople(placeGroupManagementUnderPeople(placeRoomBookingsUnderCalendar(placeAttendanceUnderCalendar(items))));
}

/** Every [prefix, module] pair the next chassis's navigation claims, with the placements applied. */
const NEXT_CLAIMS: ReadonlyArray<readonly [prefix: string, module: NavModuleId | "memberPortal"]> = (() => {
  const entries = (Object.keys(NAV_MODULE_ROUTES) as NavModuleId[]).map((module) => ({ module, ...navRoute(module) }));
  const claims: Array<readonly [string, NavModuleId | "memberPortal"]> = [];
  for (const item of placeForNextChassis(entries)) {
    for (const prefix of [item.href, ...(item.matchPrefixes ?? [])]) claims.push([prefix, item.module]);
  }
  for (const prefix of MEMBER_PORTAL_PREFIXES) claims.push([prefix, "memberPortal"]);
  return claims;
})();

/**
 * The module the next chassis's navigation puts `pathname` in: the module of the prefix that
 * matches it, matched as the sidebar matches it (the prefix itself or anything under it). No two
 * modules claim overlapping prefixes (the sidebar would highlight both: module-nav-routes.test.ts),
 * so there is at most one. `null` for a page the navigation does not place anywhere.
 */
export function navModuleForNextPath(pathname: string): NavModuleId | "memberPortal" | null {
  const path = pathname.toLowerCase();
  return NEXT_CLAIMS.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`))?.[1] ?? null;
}
